import { MutationOptions, useMutation, useQuery, useQueryClient } from "react-query";
import { API_URL, APIError, APIResponse, get, postFormData } from "../api";

export interface TravelDocument {
  number: string;
  issuing_country: string | null;
  expires_on: string | null;
  verified: boolean;
  has_document: boolean;
}

export interface TravelDocumentInput {
  number: string;
  issuing_country: string;
  expires_on: string;
  document: File;
}

/**
 * Account-level passport KYC for an international resident. It is deliberately
 * separate from the passenger data sent to a flight supplier.
 */
export function useTravelDocument(enabled = true) {
  const query = useQuery<APIResponse<{ passport: TravelDocument | null }>, APIError>({
    queryKey: ["travel-document"],
    queryFn: () =>
      get(`${API_URL}/account/compliance/travel-document`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }),
    enabled,
  });

  return {
    passport: query.data?.data?.passport ?? null,
    isLoadingPassport: query.isLoading,
  };
}

export function useSaveTravelDocument(
  options?: MutationOptions<
    APIResponse<{ passport: TravelDocument; verification_available: boolean }>,
    APIError,
    TravelDocumentInput
  >
) {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (variables: TravelDocumentInput) => {
      const body = new FormData();
      body.append("number", variables.number);
      body.append("issuing_country", variables.issuing_country);
      body.append("expires_on", variables.expires_on);
      body.append("document", variables.document, variables.document.name);
      return postFormData(`${API_URL}/account/compliance/travel-document`, body, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries("travel-document");
        queryClient.invalidateQueries("kyc-requirements");
        options?.onSuccess?.(data, variables, context);
      },
    }
  );

  return {
    savePassport: mutation.mutate,
    savingPassport: mutation.isLoading,
    savePassportError: mutation.error,
    isSavePassportError: mutation.isError,
    /**
     * False for a non-Nigerian passport: the only document check configured is
     * YouVerify's Nigerian endpoint, so the document is stored honestly as
     * unverified. Surfaced so the UI can explain the state instead of showing a
     * bare "unverified" badge the customer can do nothing about.
     */
     verificationAvailable: mutation.data?.data?.verification_available ?? false,
  };
}
